import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { LinkField, LinkFieldValue } from '@sitecore-content-sdk/nextjs';

import styles from './NewsletterSignupModal.module.scss';
import { BrandAndThemeProvider } from 'components/helpers/ContextProvider/BrandAndThemeContext';
import { Themes } from 'lib/themes';
import { useNewsletterSignup } from 'lib/hooks/useNewsletterSignup';
import { Subscriptions } from 'lib/services/newsletter-signup-service';
import {
  sendNewsletterOptInAnalytics,
  sendInterstitialViewAnalytics,
  sendInterstitialCloseAnalytics,
} from 'components/helpers/Analytics/NewsletterSignupAnalytics';
import { SvgIcon } from 'components/helpers/SvgIcon';
import CtaButton from 'components/helpers/Atoms/Cta/CtaButton';
import CtaLink from 'components/helpers/Atoms/Cta/CtaLink';
import {
  canOpenNewsletterModal,
  getPageLoadCount,
  hasAcceptedPrivacyPolicy,
  setDismissedCookie,
  setUserSubscriptionsCookie,
  shouldHideFromCookie,
} from './helpers/NewsletterSignupModal';
import { useOnRouteChange } from 'lib/hooks/useOnRouteChange';
import { getFocusableElements } from 'lib/utils/dom-utils/tab';
import { useSessionSafe } from 'lib/hooks/auth/useSessionSafe';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const SHOW_BY_DEFAULT_FOR_TESTING = false;

export interface NewsletterSignUpModalProps {
  theme?: Themes;
  headline?: React.ReactNode;
  description?: React.ReactNode;
  emailLabel?: React.ReactNode;
  emailPlaceholder?: string;
  consentLabel?: React.ReactNode;
  submitButtonText?: React.ReactNode;
  requiredText?: React.ReactNode;
  requiredErrorMessage: string;
  emailErrorMessage: string;
  loginLink?: LinkField;
  loginPromptText?: React.ReactNode;
  successHeadline?: React.ReactNode;
  successDescription?: React.ReactNode;
  successCtaText?: React.ReactNode;
  successSignupLink?: (email: string) => LinkField | LinkFieldValue | undefined;
  subscriptions?: Subscriptions[];
  newsletterTitle?: string;
  subscriberPosition?: string;
  closeLabel?: string;
}

export const NewsletterSignUpModal = (props: NewsletterSignUpModalProps) => {
  const { data: session } = useSessionSafe();

  const [isClient, setIsClient] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [pageLoadCount, setPageLoadCount] = useState(0);
  const [hasScrolled, setHasScrolled] = useState(false);
  const [hasDismissed, setHasDismissed] = useState(false);
  const [email, setEmail] = useState('');
  const [submittedEmail, setSubmittedEmail] = useState('');
  const [fieldError, setFieldError] = useState<string | null>(null);

  const dialogRef = useRef<HTMLDivElement>(null);
  const firstInputRef = useRef<HTMLInputElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  const handleNewsletterSuccess = useCallback(
    (subscriptions?: Subscriptions[]) => {
      if (!subscriptions?.length) {
        return;
      }

      // Set userSubscriptions cookie (session-scoped)
      setUserSubscriptionsCookie(subscriptions);

      const newsletterTitle = props.newsletterTitle || 'Newsletter';
      sendNewsletterOptInAnalytics({
        newsletterTitles: subscriptions.map(() => newsletterTitle),
        newsletterIds: subscriptions.map((s) => s.SubscriptionId),
        subscriberPosition: props.subscriberPosition || 'Interstitial',
      });
    },
    [props.newsletterTitle, props.subscriberPosition]
  );

  const {
    isSubmitting,
    isSuccess,
    error,
    shouldShowComponent,
    isCheckingSubscription,
    submitNewsletter,
  } = useNewsletterSignup(session, props.subscriptions, props.emailErrorMessage, {
    onSuccess: handleNewsletterSuccess,
  });

  // Poll for privacy consent cookie — OneTrust sets it asynchronously after user accepts
  const [hasPrivacyConsent, setHasPrivacyConsent] = useState(() => hasAcceptedPrivacyPolicy());

  useEffect(() => {
    if (hasPrivacyConsent) return;

    const intervalId = window.setInterval(() => {
      if (hasAcceptedPrivacyPolicy()) {
        setHasPrivacyConsent(true);
        window.clearInterval(intervalId);
      }
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [hasPrivacyConsent]);

  const fireInterstitialAnalytics = useCallback(
    (eventFn: typeof sendInterstitialViewAnalytics) => {
      const newsletterTitle = props.newsletterTitle || 'Newsletter';
      const subscriptionList = props.subscriptions;

      eventFn({
        newsletterTitles: subscriptionList?.length
          ? subscriptionList.map(() => newsletterTitle)
          : [newsletterTitle],
        newsletterIds: subscriptionList?.length
          ? subscriptionList.map((s) => s.SubscriptionId)
          : [],
        subscriberPosition: props.subscriberPosition || 'Interstitial',
      });
    },
    [props.newsletterTitle, props.subscriberPosition, props.subscriptions]
  );

  useEffect(() => {
    setIsClient(true);
    setPageLoadCount(getPageLoadCount());
    setHasDismissed(shouldHideFromCookie());

    const handleScroll = () => {
      if (window.scrollY > 0) {
        setHasScrolled(true);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Increment pageCount on client-side route changes
  const handleRouteChange = useCallback(() => {
    setPageLoadCount(getPageLoadCount());
  }, []);

  useOnRouteChange(handleRouteChange, ['routeChangeStart']);

  // Once all conditions (except delay) are met, start a 6-second timer then open the modal
  useEffect(() => {
    if (!isClient || isOpen || isSuccess || isCheckingSubscription) {
      return;
    }

    const conditionsMet = canOpenNewsletterModal({
      shouldShowComponent,
      hasDismissed,
      pageLoadCount,
      hasScrolled,
      hasPrivacyConsent,
    });

    if (!conditionsMet) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      previousFocusRef.current = document.activeElement as HTMLElement;
      // set HideInterstitial cookie to prevent modal from showing again for 3 days once timer elapses, even if user doesn't interact with the modal
      setDismissedCookie();
      setIsOpen(true);
    }, 6000);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [
    hasDismissed,
    hasPrivacyConsent,
    hasScrolled,
    isCheckingSubscription,
    isClient,
    isOpen,
    isSuccess,
    pageLoadCount,
    shouldShowComponent,
  ]);

  // When modal opens: set HideInterstitial cookie (3-day expiry) and fire analytics
  useEffect(() => {
    if (isOpen && isClient) {
      setDismissedCookie();
      setHasDismissed(true);
      fireInterstitialAnalytics(sendInterstitialViewAnalytics);
    }
    // Only fire when isOpen changes to true
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const closeModal = useCallback(() => {
    fireInterstitialAnalytics(sendInterstitialCloseAnalytics);
    setDismissedCookie();
    setIsOpen(false);
    previousFocusRef.current?.focus();
  }, [fireInterstitialAnalytics]);

  useEffect(() => {
    const appRoot = document.getElementById('__next');

    if (!appRoot) {
      return;
    }

    if (isOpen) {
      appRoot.setAttribute('aria-hidden', 'true');
    } else {
      appRoot.removeAttribute('aria-hidden');
    }

    return () => {
      appRoot.removeAttribute('aria-hidden');
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    dialogRef.current?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeModal();
        return;
      }

      if (event.key !== 'Tab') {
        return;
      }

      const focusableElements = getFocusableElements(dialogRef.current);
      if (!focusableElements.length) {
        return;
      }

      const firstFocusable = focusableElements[0];
      const lastFocusable = focusableElements[focusableElements.length - 1];
      const activeElement = document.activeElement;

      if (event.shiftKey && activeElement === firstFocusable) {
        event.preventDefault();
        lastFocusable.focus();
      }

      if (!event.shiftKey && activeElement === lastFocusable) {
        event.preventDefault();
        firstFocusable.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, closeModal]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!email.trim()) {
      setFieldError(props.requiredErrorMessage);
      return;
    }

    if (!EMAIL_REGEX.test(email)) {
      setFieldError(props.emailErrorMessage);
      return;
    }

    setFieldError(null);

    setSubmittedEmail(email);
    await submitNewsletter(email, props.subscriptions);
    setEmail('');
  };

  //  For testing: bypass all gating conditions, only require client-side mount
  const shouldRenderModal = SHOW_BY_DEFAULT_FOR_TESTING
    ? isClient && isOpen
    : isClient && shouldShowComponent && isOpen;

  const successLink = props.successSignupLink?.(submittedEmail);
  const successHref =
    (successLink as LinkFieldValue)?.href ?? (successLink as LinkField)?.value?.href ?? '';
  const successLinkText =
    (successLink as LinkFieldValue)?.text ?? (successLink as LinkField)?.value?.text ?? '';

  if (!shouldRenderModal) {
    return null;
  }

  return createPortal(
    <BrandAndThemeProvider theme={props.theme}>
      <div
        className={styles.overlay}
        onClick={(event) => {
          if (event.target === event.currentTarget) {
            closeModal();
          }
        }}
        onWheel={(event) => {
          if (event.target === event.currentTarget) {
            window.scrollBy({ top: event.deltaY, behavior: 'auto' });
          }
        }}
      >
        <div
          ref={dialogRef}
          className={styles.modal}
          role="dialog"
          aria-modal="true"
          aria-labelledby="newsletter-modal-title"
          aria-describedby="newsletter-modal-description"
          data-component="authorable/shared/newsletter-signup-modal/newsletter-signup-modal"
          tabIndex={-1}
        >
          <button type="button" className={styles.closeButton} onClick={closeModal}>
            <span className={styles.visuallyHidden}>{props.closeLabel || 'Close'}</span>
            <SvgIcon icon="close" size="xs" aria-hidden="true" />
          </button>

          {/* Persistent live region — always in the DOM so screen readers detect content changes */}
          <div
            className={styles.visuallyHidden}
            role="status"
            aria-live="polite"
            aria-atomic="true"
          >
            {isSuccess ? 'Success. Your email has been submitted.' : ''}
          </div>

          {isSuccess ? (
            <div className={styles.successState}>
              {props.successHeadline && (
                <h2 id="newsletter-modal-title" className={styles.headline}>
                  {props.successHeadline}
                </h2>
              )}
              {props.successDescription && (
                <div id="newsletter-modal-description" className={styles.description}>
                  {props.successDescription}
                </div>
              )}
              {props.successSignupLink && (
                <div className={styles.successCta}>
                  <CtaLink
                    className={styles.successSignupLink}
                    url={successHref}
                    text={successLinkText}
                    role="button"
                    ctaStyle="filled"
                    ctaVariant="inverse"
                  />
                </div>
              )}
            </div>
          ) : (
            <>
              {props.headline && (
                <h2 id="newsletter-modal-title" className={styles.headline}>
                  {props.headline}
                </h2>
              )}

              {props.description && (
                <div id="newsletter-modal-description" className={styles.description}>
                  {props.description}
                </div>
              )}
              {/* form */}
              <form onSubmit={handleSubmit} noValidate className={styles.formWrapper}>
                <div className={styles.requiredText}>{props.requiredText}</div>
                <div className={styles.formGroup}>
                  <div className={styles.formFields}>
                    {props.emailLabel && (
                      <label htmlFor="newsletter-signup-modal-email" className={styles.emailLabel}>
                        {props.emailLabel}
                      </label>
                    )}
                    <input
                      ref={firstInputRef}
                      id="newsletter-signup-modal-email"
                      type="email"
                      required
                      placeholder={props.emailPlaceholder}
                      value={email}
                      onChange={(event) => {
                        setEmail(event.target.value);
                        setFieldError(null);
                      }}
                      className={fieldError || error ? styles.emailInputError : styles.emailInput}
                      aria-required="true"
                      aria-invalid={!!(fieldError || error)}
                      aria-describedby={
                        fieldError || error ? 'newsletter-signup-modal-error' : undefined
                      }
                      disabled={isSubmitting}
                    />

                    {fieldError && (
                      <div
                        id="newsletter-signup-modal-error"
                        className={styles.errorText}
                        role="alert"
                        aria-live="assertive"
                      >
                        {fieldError}
                      </div>
                    )}

                    {!fieldError && error && (
                      <div
                        id="newsletter-signup-modal-error"
                        className={styles.errorText}
                        role="alert"
                        aria-live="assertive"
                      >
                        {error}
                      </div>
                    )}
                  </div>

                  <div className={styles.consentRow}>
                    <label
                      htmlFor="newsletter-signup-modal-consent"
                      className={styles.consentLabel}
                    >
                      {props.consentLabel}
                    </label>
                  </div>
                </div>
                <div className={styles.formCta}>
                  <CtaButton
                    type="submit"
                    ctaStyle="filled"
                    ctaVariant="brand"
                    disabled={isSubmitting}
                  >
                    {props.submitButtonText}
                  </CtaButton>
                </div>

                {(props.loginPromptText || props.loginLink) && (
                  <div className={styles.loginRow}>
                    {props.loginPromptText && (
                      <span className={styles.loginPrompt}>{props.loginPromptText}</span>
                    )}
                    <CtaLink
                      url={props.loginLink?.value?.href}
                      text={props.loginLink?.value?.text}
                      ctaStyle="link"
                      ctaVariant="brand"
                      className={styles.loginLink}
                      onClick={closeModal}
                    />
                  </div>
                )}
              </form>
            </>
          )}
        </div>
      </div>
    </BrandAndThemeProvider>,
    document.body
  );
};
